import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Title1,
  Text,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { getArchitectureSummary, type ArchitectureSummary } from '../api/architecture';
import { ArchitectureFlowDiagram } from '../components/ArchitectureFlowDiagram';

// Workbench MVP Session A — Architecture Explorer.
// Runtime-aware topology + per-node stats table over a rolling window.

const useStyles = makeStyles({
  header: { marginBottom: '18px' },
  kpiGrid: {
    display: 'grid',
    gap: '14px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    marginBottom: '18px',
  },
  kpi: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
  },
  kpiLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  kpiValue: {
    fontSize: '26px',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums' as const,
    marginTop: '4px',
  },
  table: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '10px 14px',
  },
  tableHeader: {
    fontWeight: 600,
    marginBottom: '10px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1.4fr 0.8fr 0.9fr 0.9fr 0.8fr 0.8fr 0.9fr',
    gap: '10px',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  rowHead: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  err: { color: tokens.colorPaletteRedForeground1 },
});

function fmtMs(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${v.toFixed(0)}ms`;
}

function statusBadge(status: string) {
  if (status === 'healthy') return <Badge appearance="tint" color="success">healthy</Badge>;
  if (status === 'degraded') return <Badge appearance="tint" color="warning">degraded</Badge>;
  return <Badge appearance="tint" color="subtle">idle</Badge>;
}

export default function ArchitectureExplorer() {
  const s = useStyles();
  const [summary, setSummary] = useState<ArchitectureSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getArchitectureSummary(7);
        if (mounted) setSummary(data);
      } catch (e) {
        if (mounted) setErr((e as Error).message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) return <Spinner label="Loading architecture snapshot…" />;
  if (err) return <div className={s.err}>Error: {err}</div>;
  if (!summary) return null;

  return (
    <div>
      <div className={s.header}>
        <Title1>Architecture Explorer</Title1>
        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
          Runtime topology over the last {summary.windowDays} day{summary.windowDays === 1 ? '' : 's'}.
        </Text>
      </div>

      <div className={s.kpiGrid}>
        <div className={s.kpi}>
          <div className={s.kpiLabel}>Traces</div>
          <div className={s.kpiValue}>{summary.totalTraceCount}</div>
        </div>
        <div className={s.kpi}>
          <div className={s.kpiLabel}>Steps observed</div>
          <div className={s.kpiValue}>{summary.totalStepCount}</div>
        </div>
        <div className={s.kpi}>
          <div className={s.kpiLabel}>Errors</div>
          <div className={s.kpiValue}>{summary.totalErrorCount}</div>
        </div>
      </div>

      <ArchitectureFlowDiagram stats={summary.nodeStats} />

      <div className={s.table}>
        <div className={s.tableHeader}>Per-node runtime</div>
        <div className={`${s.row} ${s.rowHead}`}>
          <div>Node</div>
          <div>Status</div>
          <div style={{ textAlign: 'right' }}>Invocations</div>
          <div style={{ textAlign: 'right' }}>Avg</div>
          <div style={{ textAlign: 'right' }}>P95</div>
          <div style={{ textAlign: 'right' }}>Errors</div>
          <div style={{ textAlign: 'right' }}>SLA breaches</div>
        </div>
        {summary.nodeStats.map((n) => (
          <div key={n.nodeId} className={s.row}>
            <div>{n.nodeId}</div>
            <div>{statusBadge(n.status)}</div>
            <div style={{ textAlign: 'right' }}>{n.invocationCount}</div>
            <div style={{ textAlign: 'right' }}>{fmtMs(n.avgLatencyMs)}</div>
            <div style={{ textAlign: 'right' }}>{fmtMs(n.p95LatencyMs)}</div>
            <div style={{ textAlign: 'right' }}>{n.errorCount}</div>
            <div style={{ textAlign: 'right' }}>{n.slaBreachCount}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
